(() => {
  'use strict';
  function create(grid, renderCard, onNavigate) {
    const batchSize = 48;
    const cards = new Map();
    const end = document.querySelector('[data-collection-end]');
    const moreButton = end?.querySelector('[data-collection-more]');
    let products = [];
    let order = [];
    let renderedCount = 0;
    let imageSizes = '';
    let columns = 7;
    let appendScheduled = false;

    function measureImages() {
      const mobile = window.matchMedia('(max-width: 768px)').matches;
      columns = mobile && !grid.closest('.collection-page--kiosk')
        ? 2 : (Number(grid.dataset.columns) || 7);
      const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
      // The grid is hidden during inventory verification; its parent still has its final width.
      const width = grid.clientWidth || grid.parentElement.clientWidth;
      const size = Math.max(1, Math.ceil((width - gap * (columns - 1)) / columns - 2));
      return `${size}px`;
    }

    function refreshImages() {
      const nextSizes = measureImages();
      if (nextSizes === imageSizes) return;
      imageSizes = nextSizes;
      cards.forEach(card => {
        const image = card.querySelector('img');
        if (image) image.sizes = imageSizes;
      });
    }

    function updateProgress() {
      const complete = renderedCount >= products.length;
      end.hidden = complete;
      // Automatic appends can move the end marker while a pointer approaches it.
      // Keep the explicit fallback button only when automatic observation is unavailable.
      moreButton.hidden = complete || Boolean(observer);
    }

    function prepareCards(start, end) {
      refreshImages();
      const missing = products.slice(start, end).filter(product => !cards.has(product.id));
      if (missing.length) {
        const template = document.createElement('template');
        template.innerHTML = missing.map((product, index) => renderCard(product, {
          sizes: imageSizes,
          eager: start === 0 && index < columns
        })).join('');
        [...template.content.children].forEach(card => cards.set(Number(card.dataset.productId), card));
      }
      const fragment = document.createDocumentFragment();
      products.slice(start, end).forEach(product => {
        const card = cards.get(product.id);
        if (card) fragment.appendChild(card);
      });
      return fragment;
    }

    function append(count, focusFirst = false) {
      const start = renderedCount;
      const end = Math.min(products.length, start + count);
      if (end <= start) return;
      grid.appendChild(prepareCards(start, end));
      renderedCount = end;
      updateProgress();
      if (focusFirst) cards.get(products[start].id)?.querySelector('.product-card__link')?.focus();
      observeEnd();
    }

    function scheduleAppend() {
      if (appendScheduled || document.hidden || end?.contains(document.activeElement)) return;
      appendScheduled = true;
      requestAnimationFrame(() => {
        appendScheduled = false;
        if (!end || end.hidden || renderedCount >= products.length) return;
        const bounds = end.getBoundingClientRect();
        if (bounds.top <= window.innerHeight + 600 && bounds.bottom >= 0) append(batchSize);
      });
    }

    const observer = end && 'IntersectionObserver' in window
      ? new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) scheduleAppend();
        }, { rootMargin: '600px 0px' }) : null;

    function observeEnd() {
      if (!observer) return;
      observer.unobserve(end);
      if (renderedCount < products.length) observer.observe(end);
    }

    moreButton?.addEventListener('click', () => append(batchSize, true));
    if ('ResizeObserver' in window) new ResizeObserver(refreshImages).observe(grid.parentElement);
    else window.addEventListener('resize', refreshImages, { passive: true });
    new MutationObserver(refreshImages).observe(grid, { attributes: true, attributeFilter: ['data-columns'] });

    function setLink(event) {
      const link = event.target.closest('[data-set-link]');
      if (!link || !grid.contains(link)) return false;
      event.preventDefault();
      event.stopPropagation();
      onNavigate();
      window.location.href = link.dataset.setLink;
      return true;
    }
    grid.addEventListener('click', event => {
      if (!setLink(event) && event.target.closest('.product-card__link')) onNavigate();
    });
    grid.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') setLink(event);
    });
    grid.addEventListener('focusin', event => {
      const card = event.target.closest('.product-card');
      const lastRows = products.slice(Math.max(0, renderedCount - columns * 2), renderedCount);
      if (lastRows.some(product => String(product.id) === card?.dataset.productId)) append(batchSize);
    });

    function render(nextProducts, minimum = 0) {
      const nextOrder = nextProducts.map(product => product.id);
      const unchanged = order.length === nextOrder.length && order.every((id, index) => id === nextOrder[index]);
      products = nextProducts;
      if (unchanged) {
        append(Math.max(0, minimum - renderedCount));
        updateProgress();
        return false;
      }
      // Filter/sort the complete catalog, but only build the first batch of that result.
      // Previously visited cards stay reusable; appending never replaces existing nodes.
      renderedCount = Math.min(products.length, Math.max(batchSize, minimum));
      grid.replaceChildren(prepareCards(0, renderedCount));
      order = nextOrder;
      updateProgress();
      observeEnd();
      return true;
    }
    return { render, getRenderedCount: () => renderedCount };
  }
  window.TMMCollectionGrid = { create };
})();
